import { formatMoney } from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import type { AiCredit } from "../data";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/**
 * AI credit of the workshop: what is left this month, and buying an extra pack
 * (owner or manager; the price goes on the monthly bill, confirmed once per purchase).
 */
export function AiCreditPanel({ canBuy }: { canBuy: boolean }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [credit, setCredit] = useState<AiCredit | null>(null);
  const [pack, setPack] = useState<number | null>(null);
  const [bought, setBought] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const money = (huf: number) => formatMoney(huf, "HUF", i18n.language);

  const load = useCallback(async () => {
    try {
      setCredit(await store.aiCredit(workshop.id));
    } catch (err) {
      setError(err);
    }
  }, [store, workshop.id]);
  useEffect(() => void load(), [load]);
  if (!credit) return <ErrorText error={error} />;

  const buy = async () => {
    if (pack === null) return;
    setError(null);
    try {
      await store.buyAiCredit(workshop.id, pack);
      setPack(null);
      setBought(true);
      await load();
    } catch (err) {
      setError(err);
    }
  };
  const low = credit.available <= 0;

  return (
    <section className="ai-credit" aria-label={t("aiCredit.title")}>
      <h2>
        {t("aiCredit.title")} <Help id="aiCredit" />
      </h2>
      <p className={low ? "warn-text" : undefined}>
        {low ? t("aiCredit.empty") : t("aiCredit.available", { amount: money(credit.available) })}
      </p>
      <p className="muted">
        {t("aiCredit.details", { allowance: money(credit.allowance), used: money(credit.usedThisMonth), bought: money(credit.boughtLeft) })}
      </p>
      <ErrorText error={error} />
      {bought && <p className="ok">{t("aiCredit.bought")}</p>}
      {canBuy && (
        <>
          <fieldset className="choice-cards">
            <legend>{t("aiCredit.buy")}</legend>
            {credit.packs.map((p) => (
              <label key={p} className={`choice-card${pack === p ? " selected" : ""}`}>
                <input type="radio" name="aiPack" checked={pack === p} onChange={() => { setPack(p); setBought(false); }} />
                <span><strong>{money(p)}</strong></span>
              </label>
            ))}
          </fieldset>
          {pack !== null && (
            <div className="paid-accept" role="group" aria-label={t("aiCredit.buy")}>
              <p>{t("aiCredit.confirm", { amount: money(pack) })}</p>
              <div className="actions">
                <button type="button" className="primary" onClick={() => void buy()}>{t("aiCredit.confirmButton", { amount: money(pack) })}</button>
                <button type="button" onClick={() => setPack(null)}>{t("common.cancel")}</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
